//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { Auth } from 'aws-amplify';

import { CuralateAuthTokenHeader } from '@constants/CuralateAuthTokenHeader';
import { isDefined } from '@utils/filterPredicates';
import { getQuerySeparator } from '@utils/getQuerySeparator';
import {
  ISerializedResponse,
  makeSerializedResponse,
} from '@utils/types/SerializedResponse';

import {
  IBaseUrls,
  IBasicCuralateSession,
  ICuralateSession,
} from 'app/basic-session/ICuralateSession';
import { crl8, isAuthedSession } from 'app/curalate.globals';
import logger from 'app/logger/logger';

/**
 * Internal-only interface describing the Web-gateway common error formats.
 * sessionFetch consumers should code against the consolidated RequestError shape instead.
 *
 * There are two formats for errors:
 * 1. The first format is defined here: https://github.com/curalate/deathstar/blob/master/core-libraries/finatra/server/src/main/scala/com/curalate/finatra/framework/server/exceptions/ExceptionMapperBase.scala#L12
 * 2. The second format is used in multiple places, an example can be found here: https://github.com/curalate/curalate-social-listening-gateway-service/blob/5ec9997ccbdc1b6bcae24e301e23e12ece3ae730/model/src/main/scala/com/curalate/services/social_listening_gateway/model/api/v1/internal/SocialListeningGatewayResponse.scala#L45
 */

interface NestedWebGatewayErrorDto {
  message: string;
  type?: string;
  errorId?: string;
  context?: unknown;
}

interface WebGatewayErrorNestedErrorDto {
  error: NestedWebGatewayErrorDto;
}

interface WebGatewayErrorDto {
  msg: string;
  errorId?: string;
  context?: unknown;
}

export type WebGatewayError =
  | WebGatewayErrorNestedErrorDto
  | WebGatewayErrorDto;

/**
 * Internal-only interface describing the LWS common error format.
 * sessionFetch consumers should code against the consolidated `RequestError` shape instead.
 */
interface IPinsightsErrorDto {
  errorId: string;
  message: string;
  errorCode?: number;
}

function createPinsightsError(res: Response, error: IPinsightsErrorDto) {
  const message = error.message
    ? error.message
    : 'Pinsights sessionFetch request failed';
  const errorId = error.errorId;
  const errorCode = error.errorCode;
  return new RequestError(message, errorId, res.status, errorCode);
}

/**
 * Type guard to check if an unknown error is a WebGateway error
 * @param err - Unknown error to validate
 * @returns Type assertion for WebGatewayError
 */
export const isWebGatewayError = (err: unknown): err is WebGatewayError => {
  if (!err || typeof err !== 'object') {
    return false;
  }

  // Check for nested error
  if ('error' in err && typeof err.error === 'object' && err.error) {
    const nested = err.error as Record<string, unknown>;
    return 'message' in nested && typeof nested.message === 'string';
  }

  // Check for WebGatewayErrorDto
  return (
    'msg' in err && typeof (err as Record<string, unknown>).msg === 'string'
  );
};

/**
 * Create a standardized RequestError from any web gateway error format
 */
function createWebGatewayError(
  res: Response,
  error: WebGatewayError,
): RequestError {
  const defaultErrorMessage = 'Web Gateway sessionFetch request failed';

  // Handle nested error formats
  if ('error' in error) {
    const { context, errorId, message } = error.error;
    return new RequestError(
      message || defaultErrorMessage,
      errorId,
      res.status,
      undefined,
      context,
    );
  }

  // Handle direct error format
  const { context, errorId, msg } = error;
  return new RequestError(
    msg || defaultErrorMessage,
    errorId,
    res.status,
    undefined,
    context,
  );
}

/**
 * Standardized error type for Curalate API Requests.
 * Consolidates error fields from API services.
 *
 * You can use `instanceof` to safely check for this error type, use
 * the `getRequestErrorMessage` helper to safely retrieve a message from it.
 */
export class RequestError extends Error {
  constructor(
    message: string,
    public errorId?: string,
    public statusCode?: number,
    public errorCode?: number,
    public context?: unknown,
  ) {
    super(message);

    // This is required because the TS config compilation target is set to 'es5'
    // Can be removed once the compilation target is changed to ES2015 or later
    // more info: https://www.dannyguo.com/blog/how-to-fix-instanceof-not-working-for-custom-errors-in-typescript/
    Object.setPrototypeOf(this, RequestError.prototype);
  }
}

function isLocalApiCall(url: string) {
  /*
    use an anchor to convert relative urls (/api) to absolute
    so that we can check their origin
  */
  const anchor = document.createElement('a');
  anchor.href = url;
  return anchor.href.startsWith(window.location.origin);
}

function isGatewayApiCall(url: string, session: IBasicCuralateSession) {
  const gatewayUrlKeys = Object.keys(session.baseUrls) as (keyof IBaseUrls)[];
  const includesGatewayUrl = gatewayUrlKeys.find((gatewayUrlKey) =>
    url.startsWith(session.baseUrls[gatewayUrlKey]),
  );
  return includesGatewayUrl !== undefined;
}

function determineRequestTypes(url: string, session?: IBasicCuralateSession) {
  const hasSession = isDefined(session);
  const isLocalRequest = isLocalApiCall(url);
  const isGatewayRequest = hasSession && isGatewayApiCall(url, session);
  return {
    isLocalRequest,
    isGatewayRequest,
  };
}

function createDefaultRequestOptions(
  requestInit: RequestInit,
  headers: Record<string, string>,
  useFetchHeader?: boolean,
): { requestInit: RequestInit; headers: Record<string, string> } {
  const newRequestInit = { ...requestInit };
  const newHeaders = { ...headers };

  if (!newRequestInit.credentials) {
    newRequestInit.credentials = 'same-origin';
  }

  if (!newHeaders['Content-Type'] && !useFetchHeader) {
    newHeaders['Content-Type'] = 'application/json';
  }

  return { requestInit: newRequestInit, headers: newHeaders };
}

async function setAuthHeader(
  headers: Record<string, string>,
  url: string,
): Promise<Record<string, string>> {
  const headersWithJWT = { ...headers };
  try {
    const cognitoJwt = (await Auth.currentSession()).getIdToken().getJwtToken();
    headersWithJWT[CuralateAuthTokenHeader] = cognitoJwt;
  } catch (e) {
    logger.console.debug(`Couldn't build jwt header for request to ${url}`, e);
    window.location.href = '/login';
  }
  return headersWithJWT;
}

async function handleResponseErrors(
  res: Response,
  url: string,
  isLocalRequest: boolean,
  isGatewayRequest: boolean,
) {
  if (!res.ok) {
    if (isLocalRequest) {
      const error = (await res.json()) as IPinsightsErrorDto;
      throw createPinsightsError(res, error);
    } else if (isGatewayRequest) {
      // Handle gateway errors
      const error: unknown = await res.json();
      if (isWebGatewayError(error)) {
        throw createWebGatewayError(res, error);
      }
      throw new RequestError(
        'Web Gateway error of unknown format',
        undefined,
        res.status,
      );
    } else {
      throw new RequestError(
        `Sessionfetch ${res.status} error from unknown request source, url: ${url}`,
      );
    }
  }
}

/**
 * Make an HTTP request to a web gateway service.
 * @deprecated Prefer serializedSessionFetch, which has better type checking.
 *
 * set useFetchHeader to true if you would like to use the 'Content-Type' generated by fetch.
 * This is necessary for multipart form data to get the generated boundary.
 * More info: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Type#content-type_in_multipart_forms
 */
export async function sessionFetch(
  url: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<Response> {
  const session = crl8.session as
    | ICuralateSession
    | IBasicCuralateSession
    | undefined;

  const requestInit = init ?? ({} as RequestInit);
  const headers: Record<string, string> =
    (init && (init.headers as Record<string, string>)) ?? {};

  const { headers: updatedHeaders, requestInit: updatedRequestInit } =
    createDefaultRequestOptions(requestInit, headers, useFetchHeader);

  const { isGatewayRequest, isLocalRequest } = determineRequestTypes(
    url,
    session,
  );

  const updatedHeadersWithAuth = await setAuthHeader(updatedHeaders, url);

  const safeClientId =
    (isAuthedSession(session) &&
      `${getQuerySeparator(url)}clientId=${session.client.id}`) ||
    '';

  const targetUrl = `${url}${safeClientId}`;

  const res = await fetch(targetUrl, {
    ...updatedRequestInit,
    headers: new Headers(updatedHeadersWithAuth),
  });

  await handleResponseErrors(res, url, isLocalRequest, isGatewayRequest);

  return res;
}

/**
 * Makes a fetch request without a JWT token, with optional custom headers and request initialization.
 *
 * @param url - The URL to which the request is sent.
 * @param init - Optional. The custom settings to apply to the request.
 * @param useFetchHeader - Optional. A boolean indicating whether to use fetch headers.
 * @returns A promise that resolves to the response of the fetch request.
 *
 */
export async function sessionFetchNoJWT(
  url: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<Response> {
  const session = crl8.session as
    | ICuralateSession
    | IBasicCuralateSession
    | undefined;

  const requestInit = init ?? ({} as RequestInit);
  const headers: Record<string, string> =
    (init && (init.headers as Record<string, string>)) ?? {};

  const { headers: updatedHeaders, requestInit: updatedRequestInit } =
    createDefaultRequestOptions(requestInit, headers, useFetchHeader);

  const { isGatewayRequest, isLocalRequest } = determineRequestTypes(
    url,
    session,
  );

  const targetUrl = `${url}`;

  const res = await fetch(targetUrl, {
    ...updatedRequestInit,
    headers: new Headers(updatedHeaders),
  });

  await handleResponseErrors(res, url, isLocalRequest, isGatewayRequest);

  return res;
}

/**
 * Make an HTTP request to LWS.
 */
export async function serializedSessionFetch(
  url: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> {
  const res = await sessionFetch(url, init, useFetchHeader);
  return makeSerializedResponse(res);
}

/**
 * Make an HTTP request to a web gateway service.
 * @deprecated Prefer serializedGatewayFetch, which has better type checking.
 */
export function gatewayFetch(
  service: keyof IBaseUrls,
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<Response> {
  return sessionFetch(
    `${crl8.session?.baseUrls[service]}${endpoint}`,
    init,
    useFetchHeader,
  );
}

/**
 * Make an HTTP request to a non JWTFetch service.
 *
 * DISCLAIMER: Use this method only for services that explicitly do not require JWT authentication.
 * Misuse of this method with services that require JWT authentication will result in failed requests.
 */
export function gatewayNoJWTFetch(
  service: keyof IBaseUrls,
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<Response> {
  return sessionFetchNoJWT(
    `${crl8.session?.baseUrls[service]}${endpoint}`,
    init,
    useFetchHeader,
  );
}

/**
 * Make an HTTP request to a web gateway service.
 */
export async function serializedGatewayFetch(
  service: keyof IBaseUrls,
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> {
  const res = await sessionFetch(
    `${crl8.session?.baseUrls[service]}${endpoint}`,
    init,
    useFetchHeader,
  );
  return makeSerializedResponse(res);
}

// Export sessionFetch on the crl8 object
// to help devs who need to debug things.
// We don't have a Swagger UI for LWS so this is as close as we can get to helpful there!
(crl8 as { _fetch?: typeof sessionFetch })._fetch = sessionFetch;
